"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle,
  Anchor,
  ArrowRight,
  BadgeCheck,
  BookOpenCheck,
  Compass,
  CreditCard,
  FileText,
  FileWarning,
  GraduationCap,
  LifeBuoy,
  MessageSquare,
  Route,
  ScanSearch,
  ShieldCheck,
  Ship,
  TrendingUp,
  UserCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import Breadcrumbs from "@/components/seo/Breadcrumbs";
import type { PublicStats } from "@/lib/api/content";
import { FAQ_ITEMS } from "./faq-data";

const REGISTER_HREF = "/register?role=seafarer";

// ─── Copy blocks ─────────────────────────────────────────────────────────────

const PAIN_POINTS: { title: string; body: string; Icon: LucideIcon }[] = [
  {
    title: "Agents demanding money up front",
    body: "Placement fees, 'processing charges', visa costs, medical fees — requested before you have signed anything. Under MLC 2006 these charges are prohibited, yet they remain the single most common way seafarers lose money. A chief officer in Kochi pays for a berth that never existed. A cadet in Manila pays for a 'guaranteed slot' on a vessel that was never hiring.",
    Icon: CreditCard,
  },
  {
    title: "Vacancies that were never real",
    body: "Screenshots of job offers circulate in WhatsApp and Telegram groups with urgent joining dates and salaries above market. The vessel name checks out — it was copied from an AIS site — but the employer, the berth and the contract do not. By the time the 'joining port' story falls apart, your passport scan and certificates are in a stranger's hands.",
    Icon: FileWarning,
  },
  {
    title: "Contracts that change after you sign",
    body: "You sign for one wage scale and rank; the articles on board say another. The promised six-month contract becomes nine. Repatriation terms vanish. Without a clear, written contract reviewed before travel — rank, wages, vessel, duration, deductions, repatriation — you are negotiating from a ship in the middle of an ocean passage.",
    Icon: FileText,
  },
  {
    title: "The CV black hole",
    body: "You email your CV to forty crewing addresses and hear back from none. No acknowledgement, no shortlist, no rejection. You cannot tell whether the berth was filled, whether your certificates were the problem, or whether anyone read the email at all. So you send forty more, and update nothing, because nobody told you what was missing.",
    Icon: ScanSearch,
  },
  {
    title: "Certificates expiring while you wait",
    body: "Between contracts, the clock keeps running. Your tanker endorsement, your medical, your flag endorsement for the one registry the next employer needs — each lapses quietly, and each lapse quietly removes you from shortlists. Most seafarers track this in a notebook or a phone reminder. It deserves better tooling than that.",
    Icon: BookOpenCheck,
  },
  {
    title: "No map for what comes after",
    body: "Ask ten senior officers what a chief officer can do ashore and you will get ten guesses. The industry is full of roles your sea time already qualifies you for — port operations, superintendence, vetting, crewing — but nobody shows you the readiness, the gap, or the route. Careers drift sideways when they could move deliberately.",
    Icon: Compass,
  },
];

const TRUST_MEASURES: { title: string; body: string; Icon: LucideIcon }[] = [
  {
    title: "Employers are verified before they post",
    body: "Companies on CrewAhead provide structured company information — registered details, company type, and licence information where the jurisdiction requires it, such as an RPSL licence for Indian recruitment and placement services. Vacancies come from identifiable companies, not anonymous posters.",
    Icon: BadgeCheck,
  },
  {
    title: "Seafarers never pay. That is the rule, not a promise.",
    body: "The Maritime Labour Convention prohibits charging seafarers for recruitment or placement. CrewAhead is built on the same principle: your profile, your applications and your messages are free. If anyone asks you for money in connection with a vacancy you found here, report it and we will review the account.",
    Icon: ShieldCheck,
  },
  {
    title: "Reports are reviewed, not ignored",
    body: "Every reported concern goes into a moderation review against our Community Guidelines. Employers who misrepresent vacancies, demand fees or misuse documents lose access to the platform. Your report also protects the next seafarer who would have received the same message.",
    Icon: AlertTriangle,
  },
  {
    title: "Your documents stay yours",
    body: "Certificates, CDC and identity documents live in your document vault. You share them in the context of a real application to a verified company — not as scans forwarded around group chats. Originals never leave your hands, and you can see where copies have gone.",
    Icon: UserCheck,
  },
];

const STEPS: { step: string; title: string; body: string }[] = [
  {
    step: "01",
    title: "Build your profile once",
    body: "Rank, department, CoC and flag endorsements, STCW certificates, sea service by vessel type, documents with expiry dates, and your availability. It takes about fifteen minutes, and it replaces every version of your CV you have ever emailed. Structured fields mean an employer can read your readiness in seconds.",
  },
  {
    step: "02",
    title: "Get matched on maritime reality",
    body: "Matching runs on the things crewing actually decides on: your rank against the vacancy's rank, your vessel-type experience against its fleet, your certificate validity against its requirements, your availability against its joining date. A second engineer with chemical tanker time sees chemical tanker berths — not a wall of irrelevant listings.",
  },
  {
    step: "03",
    title: "Apply and talk directly",
    body: "Apply in one click with your profile and verified documents attached. Message the employer inside the platform — ask about the vessel, the crew, the contract terms — and keep the whole thread in one place. No forwarded emails, no lost CVs, no agent in the middle deciding what you get to know.",
  },
  {
    step: "04",
    title: "See the path beyond this contract",
    body: "Career intelligence maps your sea time to shore roles with readiness scores and skill gaps, so every contract is also a step somewhere. Whether the next move is master, a tanker upgrade, or a shore role in port operations, you can see what it takes before you need it.",
  },
];

const RANK_GROUPS: {
  title: string;
  intro: string;
  ranks: { name: string; note: string }[];
}[] = [
  {
    title: "Deck department jobs",
    intro:
      "Deck officers carry the commercial face of the ship — cargo, navigation, port interface — and the vacancies reflect that specificity. Master mariner vacancies ask for command time on type; a chief officer job on an oil or chemical tanker asks for tanker endorsements and senior tanker time under the officer matrix; second and third officer berths are where ECDIS, cargo work and watchkeeping experience get built and weighed.",
    ranks: [
      { name: "Master / Captain", note: "command vacancies with flag and trade requirements stated up front" },
      { name: "Chief Officer", note: "cargo and stability roles across tanker, container, bulk and offshore" },
      { name: "Second Officer", note: "navigation watchkeeping berths with clear promotion visibility" },
      { name: "Third Officer", note: "junior officer berths that state training and upgrade support" },
      { name: "Deck Cadet", note: "structured cadetships from verified companies — never sold by agents" },
    ],
  },
  {
    title: "Engine department jobs",
    intro:
      "Engine room hiring is machinery-specific in a way generic job boards never capture. A chief engineer vacancy on a bulk carrier with ME-C plants is a different profile from one on an LNG carrier with dual-fuel propulsion. Second engineer jobs on tankers carry cargo-pump and IG system expectations; ETO roles live or die on high-voltage certification and automation experience. CrewAhead vacancies state the vessel type and certificate requirements so you are matched on the machinery you actually know.",
    ranks: [
      { name: "Chief Engineer", note: "senior machinery command with propulsion-type context" },
      { name: "Second Engineer", note: "first engineer berths across tanker, bulk, container and offshore" },
      { name: "Third & Fourth Engineer", note: "watchkeeping roles with stated machinery and trade" },
      { name: "ETO / Electrician", note: "high-voltage and automation roles with certificate requirements" },
      { name: "Engine Cadet", note: "workshop-to-engineroom trainee berths from real programmes" },
    ],
  },
  {
    title: "Ratings & catering jobs",
    intro:
      "Ratings vacancies are the most scammed segment of maritime hiring, precisely because demand is high and verification is rare. Bosun, AB, oiler, fitter, pumpman and cook berths on CrewAhead come from verified companies with stated vessel types, contract durations and joining ports — not screenshots. If you hold tanker watchkeeping endorsements or DP-adjacent deck experience, your profile carries that into matching automatically.",
    ranks: [
      { name: "Bosun", note: "deck leadership berths with crew size and trade stated" },
      { name: "Able Seafarer (AB)", note: "deck watch and cargo roles across all vessel types" },
      { name: "Oiler / Motorman", note: "engine room ratings with machinery context" },
      { name: "Fitter / Pumpman", note: "specialist roles where tanker time actually counts" },
      { name: "Chief Cook", note: "catering berths with crew complement and trade route shown" },
    ],
  },
];

const VESSEL_TYPES: { name: string; body: string }[] = [
  {
    name: "Oil & chemical tankers",
    body: "Tanker berths demand the endorsements to match: oil and chemical cargo operations certificates, senior-officer tanker time for matrix compliance, and often specific vetting exposure. A chief officer with SIRE inspections under their belt or a second engineer with cargo pump and inert gas experience should be matched on exactly that — and here, they are.",
  },
  {
    name: "LNG & LPG carriers",
    body: "Gas carrier jobs sit at the top of the certification pyramid: advanced gas endorsements, cargo containment system experience, and increasingly dual-fuel engine familiarity. These berths pay accordingly and are rarely advertised on open boards. Verified employers post them with the endorsement requirements written into the vacancy.",
  },
  {
    name: "Container ships",
    body: "Container trades run on schedule pressure and rapid port turnarounds. Officers here build the high-intensity port-call experience that transfers directly to terminal and port operations ashore. Vacancies state trade lane, vessel size and ECDIS expectations rather than a generic 'container experience preferred'.",
  },
  {
    name: "Bulk carriers",
    body: "Bulk trades mean grab damage debates, hold cleanliness standards, and long ocean passages — and they remain the largest single fleet segment by hull count. Chief officer jobs here hinge on cargo planning and stability software; engineers on reliable maintenance discipline. The berths are plentiful and the matching is rank-and-type specific.",
  },
  {
    name: "Offshore vessels (PSV, AHTS, MPSV)",
    body: "Offshore hiring revolves around DP. DP officer jobs state the required certificate class and sea time; engine roles state dynamic positioning plant exposure. Rotational patterns — four weeks on, four off — are declared in the vacancy, because for offshore crew the rotation is the job.",
  },
  {
    name: "Cruise & passenger vessels",
    body: "Passenger vessel roles add crowd management, safety culture and hotel-adjacent disciplines to the standard STCW baseline. Deck and engine officers here carry an operational profile that transfers unusually well to shore-side marine management and training roles.",
  },
  {
    name: "Tugs & coastal craft",
    body: "Harbour tugs, coastal tankers and short-sea traders offer home-time-friendly patterns that deep-sea officers increasingly seek at family stages of their careers. These berths are local by nature and almost invisible on global boards — structured profiles make them findable.",
  },
  {
    name: "Ro-Ro & car carriers",
    body: "Pure car carriers and ro-pax tonnage run tight ramp operations and lashings discipline, with officer vacancies that value cargo securing experience and rapid port work. A growing fleet segment with steady demand for both deck officers and ratings.",
  },
];

const CAREER_FEATURES: { title: string; body: string; Icon: LucideIcon }[] = [
  {
    title: "A readiness score for every shore role",
    body: "Pick up to six target roles — port operations manager, technical superintendent, crewing manager, marine surveyor, vetting inspector, HSQE advisor and more — and see a live readiness score for each, calculated from your rank, sea service, vessel types and certificates. Not a personality quiz. A structured assessment against the platform's role qualification rules.",
    Icon: TrendingUp,
  },
  {
    title: "Skill gaps named, not guessed",
    body: "For each target you see two lists: the strengths your sea time already transfers — cargo planning, planned maintenance, ISM compliance, incident command — and the specific gaps to close, from terminal systems to fleet budgeting to shore-side SMS ownership. Every gap is a decision you can act on, not a vague 'get more experience'.",
    Icon: Route,
  },
  {
    title: "Courses matched to the gap",
    body: "Where a gap can be closed with training, career intelligence points at real courses from verified maritime institutes — with dates, locations and available seats. The recommendation exists because the gap analysis says you need it, not because an institute paid for a listing.",
    Icon: GraduationCap,
  },
  {
    title: "Authority-backed, not opinion-based",
    body: "Readiness assessments cite the basis they are built on — role qualification rules, industry frameworks and regulatory requirements — and flag hard barriers honestly. If a target role requires a specific licence or shore-side qualification, you see that barrier up front rather than discovering it after a year of applications.",
    Icon: ShieldCheck,
  },
];

// ─── Motion helpers ──────────────────────────────────────────────────────────

function useInViewOnce(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, options);
    observer.observe(el);
    return () => observer.disconnect();
  }, [options, inView]);

  return [ref, inView] as const;
}

function useCountUp(target: number, inView: boolean, reduced: boolean) {
  const [value, setValue] = useState(reduced ? target : 0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return;
    }
    if (!inView || startedRef.current) return;
    startedRef.current = true;
    let start: number | undefined;
    const duration = 1400;
    let raf: number;

    const step = (ts: number) => {
      if (start === undefined) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, inView, reduced]);

  return value;
}

function LiveStat({ value, label }: { value: number; label: string }) {
  const [ref, inView] = useInViewOnce({ threshold: 0.3, rootMargin: "-60px" });
  const reduced = useReducedMotion();
  const display = useCountUp(value, inView, !!reduced);

  return (
    <div ref={ref} className="flex flex-col">
      <span className="font-display text-3xl font-bold text-white sm:text-4xl">
        {display.toLocaleString("en-US")}
      </span>
      <span className="mt-1 text-sm text-ash">{label}</span>
    </div>
  );
}

function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: reduced ? 0 : 0.45, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Hero animated element: passage plot ─────────────────────────────────────
// A chart-panel style SVG: graticule, a plotted course track that draws itself
// across rank waypoints (JOIN → OOW → C/O → MASTER), and a vessel marker that
// sails the track. Reduced motion: static track, marker at final waypoint.

const TRACK_PATH =
  "M 64 336 C 140 308, 176 268, 226 236 S 320 176, 372 148 S 462 96, 496 72";

const WAYPOINTS = [
  { x: 64, y: 336, label: "JOIN" },
  { x: 226, y: 236, label: "OOW" },
  { x: 372, y: 148, label: "C/O" },
  { x: 496, y: 72, label: "MASTER" },
];

function PassagePlot() {
  const reduced = useReducedMotion();
  const last = WAYPOINTS[WAYPOINTS.length - 1];

  return (
    <div className="relative rounded-[2rem] border border-neon-cyan/15 bg-panel/70 p-4 shadow-2xl shadow-neon-cyan/10 backdrop-blur">
      <div className="rounded-[1.5rem] border border-neon-cyan/10 bg-void p-4 sm:p-5">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-neon-cyan">
            Passage plan · career track
          </p>
          <p className="hidden font-mono text-[11px] text-ash sm:block">
            CHART CA-001 · WGS-84
          </p>
        </div>

        <svg
          viewBox="0 0 560 400"
          role="img"
          aria-label="A plotted course line passing through waypoints labelled Join, OOW, Chief Officer and Master, illustrating a seafarer career track."
          className="mt-3 h-auto w-full"
        >
          {/* graticule */}
          <g stroke="#ffffff" strokeOpacity="0.05" strokeWidth="1">
            {[40, 80, 120, 160, 200, 240, 280, 320, 360].map((y) => (
              <line key={`h${y}`} x1="0" y1={y} x2="560" y2={y} />
            ))}
            {[80, 160, 240, 320, 400, 480].map((x) => (
              <line key={`v${x}`} x1={x} y1="0" x2={x} y2="400" />
            ))}
          </g>

          {/* graticule labels */}
          <g fill="#6f7488" fontSize="9" fontFamily="var(--font-mono)">
            <text x="8" y="34">19°00&apos;N</text>
            <text x="8" y="394">17°00&apos;N</text>
            <text x="506" y="394">073°00&apos;E</text>
            <text x="8" y="214" opacity="0.7">18°00&apos;N</text>
          </g>

          {/* shoal / restricted area */}
          <g>
            <circle
              cx="300"
              cy="318"
              r="46"
              fill="none"
              stroke="#ff007f"
              strokeOpacity="0.35"
              strokeWidth="1"
              strokeDasharray="4 5"
            />
            <text
              x="300"
              y="318"
              textAnchor="middle"
              fill="#ff007f"
              fillOpacity="0.6"
              fontSize="8.5"
              fontFamily="var(--font-mono)"
            >
              FEE SCAM — KEEP CLEAR
            </text>
          </g>

          {/* the career track */}
          <motion.path
            d={TRACK_PATH}
            fill="none"
            stroke="#00f3ff"
            strokeWidth="2"
            strokeDasharray="7 6"
            strokeLinecap="round"
            initial={reduced ? { pathLength: 1 } : { pathLength: 0 }}
            animate={reduced ? undefined : { pathLength: 1 }}
            transition={{ duration: 2.4, ease: "easeInOut" }}
          />

          {/* waypoints */}
          {WAYPOINTS.map((wp, i) => (
            <g key={wp.label}>
              <circle
                cx={wp.x}
                cy={wp.y}
                r="7"
                fill="none"
                stroke={i === WAYPOINTS.length - 1 ? "#00ff9d" : "#00f3ff"}
                strokeWidth="1.6"
              />
              <circle
                cx={wp.x}
                cy={wp.y}
                r="2.4"
                fill={i === WAYPOINTS.length - 1 ? "#00ff9d" : "#00f3ff"}
              />
              <text
                x={wp.x + 12}
                y={wp.y + 4}
                fill={i === WAYPOINTS.length - 1 ? "#00ff9d" : "#e5e7ef"}
                fontSize="11"
                fontWeight="700"
                fontFamily="var(--font-mono)"
              >
                {wp.label}
              </text>
            </g>
          ))}

          {/* vessel marker */}
          {reduced ? (
            <g transform={`translate(${last.x} ${last.y})`}>
              <path d="M 0 -9 L 6 8 L 0 4 L -6 8 Z" fill="#00ff9d" />
            </g>
          ) : (
            <g>
              <path d="M 0 -9 L 6 8 L 0 4 L -6 8 Z" fill="#00f3ff">
                <animateMotion
                  dur="9s"
                  repeatCount="indefinite"
                  rotate="auto"
                  path={TRACK_PATH}
                />
              </path>
            </g>
          )}
        </svg>

        <div className="mt-3 grid gap-2 border-t border-white/10 pt-3 font-mono text-[11px] text-ash sm:grid-cols-3">
          <p>
            <span className="text-neon-cyan">TRK</span> 042°T · GC
          </p>
          <p>
            <span className="text-neon-cyan">SOG</span> 12.4 kn · steady
          </p>
          <p>
            <span className="text-neon-cyan">ETA</span> your call, not an agent&apos;s
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── CTA blocks ──────────────────────────────────────────────────────────────

function PrimaryCta({ large = false }: { large?: boolean }) {
  return (
    <Link
      href={REGISTER_HREF}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl bg-neon-cyan font-bold text-obsidian shadow-glow-cyan transition hover:scale-[1.01] hover:bg-neon-cyan/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian ${
        large ? "px-8 py-4 text-base" : "px-7 py-3.5 text-sm"
      }`}
    >
      <Anchor className={large ? "h-5 w-5" : "h-4 w-4"} aria-hidden="true" />
      Create your free seafarer profile
    </Link>
  );
}

function SignInLink({ className = "" }: { className?: string }) {
  return (
    <p className={`text-sm text-ash ${className}`}>
      Already have an account?{" "}
      <Link
        href="/login"
        className="font-semibold text-neon-cyan transition-colors hover:text-neon-cyan/80"
      >
        Sign in
      </Link>
    </p>
  );
}

function MidPageCta() {
  return (
    <Reveal className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center gap-5 rounded-[2rem] border border-neon-cyan/15 bg-panel p-8 text-center md:flex-row md:justify-between md:text-left">
        <div>
          <p className="flex items-center justify-center gap-2 text-sm font-semibold text-neon-cyan md:justify-start">
            <LifeBuoy className="h-4 w-4" aria-hidden="true" />
            Free for seafarers. Always.
          </p>
          <p className="mt-2 text-lg font-bold text-white">
            Fifteen minutes to a profile employers can actually shortlist.
          </p>
        </div>
        <div className="flex flex-col items-center gap-3 md:items-end">
          <PrimaryCta />
          <SignInLink />
        </div>
      </div>
    </Reveal>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function SeafarerJobsContent({ stats }: { stats: PublicStats | null }) {
  const prefersReducedMotion = useReducedMotion();

  const heroFade = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 18 },
    visible: { opacity: 1, y: 0 },
  };

  const liveStats = stats
    ? (
        [
          { key: "seafarers", label: "seafarers on the platform", value: stats.seafarers || stats.activeSeafarers },
          { key: "seaJobs", label: "open sea-going positions", value: stats.seaJobs || stats.openPositions },
          { key: "employers", label: "verified employers", value: stats.employers },
          { key: "ranks", label: "ranks covered by matching", value: stats.ranks },
        ] as const
      ).filter((item) => item.value > 0)
    : [];

  const careerDepth = stats
    ? (
        [
          { label: "shore roles mapped", value: stats.shorePositions },
          { label: "with skill-gap guidance", value: stats.shorePositionsWithGuidance },
          { label: "skill-gap rules behind the scores", value: stats.skillGapRules },
        ] as const
      ).filter((item) => item.value > 0)
    : [];

  return (
    <main className="min-h-screen overflow-hidden bg-obsidian text-foreground">
      <Breadcrumbs />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative px-4 pb-16 pt-10 sm:px-6 sm:pt-14 lg:px-8">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_10%,rgba(0,243,255,0.12),transparent_32%),radial-gradient(circle_at_85%_25%,rgba(168,85,247,0.1),transparent_34%)]"
        />
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroFade}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-neon-cyan/20 bg-neon-cyan/8 px-4 py-2 text-sm font-semibold text-neon-cyan">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Verified employers · Zero placement fees
            </div>
            <h1 className="max-w-3xl font-display text-4xl font-bold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
              Seafarer jobs without the agent&apos;s cut, the fake vacancy, or
              the guesswork.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-ash">
              CrewAhead is a maritime careers platform where sea jobs come from
              verified employers, charging you money is against the rules, and
              every contract you take visibly moves your career somewhere — from
              deck cadet to command, or from the bridge to a shore desk. Built
              by a master mariner who got tired of watching good officers get
              scammed, stalled and second-guessed.
            </p>
            <div className="mt-9 flex flex-col gap-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <PrimaryCta large />
                <Link
                  href="/jobs"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-neon-purple/30 bg-neon-purple/8 px-7 py-4 font-bold text-neon-purple transition hover:border-neon-purple/60"
                >
                  <Ship className="h-5 w-5" aria-hidden="true" />
                  Browse open sea jobs
                </Link>
              </div>
              <SignInLink />
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroFade}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <PassagePlot />
          </motion.div>
        </div>
      </section>

      {/* ── Live stats ───────────────────────────────────────────────────── */}
      {liveStats.length > 0 && (
        <section
          aria-label="Live platform statistics"
          className="border-y border-white/5 bg-panel/30 px-4 py-12 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-7xl">
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-neon-green">
              Live from the platform — real counts, updated as you read this
            </p>
            <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {liveStats.map((item) => (
                <LiveStat key={item.key} value={item.value} label={item.label} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── The problem ──────────────────────────────────────────────────── */}
      <section aria-labelledby="broken-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-magenta">
              The honest part first
            </p>
            <h2
              id="broken-heading"
              className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl"
            >
              The way seafarers find jobs is broken. You already know this.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Most seafarers find their next contract through a chain of
              forwarded messages, a manning agent who may or may not be
              licensed, or a job board last redesigned a decade ago. The
              industry moves over a million seafarers a year through a hiring
              process that would embarrass any other profession. These are the
              failure modes — named plainly, because pretending they are rare
              is how they keep working.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PAIN_POINTS.map((point, i) => (
              <Reveal key={point.title} delay={i * 0.06}>
                <article className="group h-full rounded-xl border border-white/10 bg-panel/70 p-6 transition-colors hover:border-neon-magenta/25 hover:bg-panel-hover">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-neon-magenta/15 bg-neon-magenta/8 transition-colors group-hover:bg-neon-magenta/12">
                    <point.Icon className="h-5 w-5 text-neon-magenta" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold">{point.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{point.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Trust & fraud ────────────────────────────────────────────────── */}
      <section
        aria-labelledby="trust-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              Recruitment fraud, addressed directly
            </p>
            <h2 id="trust-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Trust is a feature. Here is exactly how it works.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Maritime recruitment fraud works because it exploits information
              asymmetry: the fraudster knows the offer is fake, and you have no
              affordable way to check. CrewAhead is designed to close that gap —
              at the platform level, not as an afterthought.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {TRUST_MEASURES.map((measure, i) => (
              <Reveal key={measure.title} delay={i * 0.06}>
                <article className="h-full rounded-xl border border-neon-cyan/10 bg-panel/70 p-6 transition-colors hover:border-neon-cyan/25">
                  <div className="flex items-start gap-4">
                    <div className="rounded-lg border border-neon-cyan/15 bg-neon-cyan/8 p-2.5">
                      <measure.Icon className="h-5 w-5 text-neon-cyan" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold">{measure.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-ash">{measure.body}</p>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <div className="rounded-xl border border-neon-amber/20 bg-neon-amber/8 p-6 sm:p-8">
              <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                <div className="max-w-2xl">
                  <h3 className="flex items-center gap-2 text-lg font-bold text-neon-amber">
                    <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                    Want the full fraud playbook?
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-ash">
                    The warning signs, the pre-acceptance checklist, how to
                    verify a company or an RPSL licence, and what to do if
                    something already feels wrong — documented in detail on our
                    dedicated safety pages.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link
                    href="/recruitment-fraud-awareness"
                    className="inline-flex items-center gap-2 rounded-lg border border-neon-amber/40 px-5 py-2.5 text-sm font-semibold text-neon-amber transition-colors hover:bg-neon-amber/10"
                  >
                    Recruitment fraud awareness
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <Link
                    href="/trust-safety"
                    className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-neon-cyan/40 hover:text-neon-cyan"
                  >
                    Trust &amp; safety
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <section aria-labelledby="how-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              How CrewAhead works
            </p>
            <h2 id="how-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              From profile to signed contract, without the middlemen.
            </h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {STEPS.map((item, i) => (
              <Reveal key={item.step} delay={i * 0.07}>
                <article className="h-full rounded-xl border border-white/10 bg-panel/70 p-6 transition-colors hover:border-neon-cyan/25 hover:bg-panel-hover">
                  <p className="font-mono text-sm font-bold text-neon-cyan">{item.step}</p>
                  <h3 className="mt-3 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{item.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MidPageCta />

      {/* ── Jobs by rank ─────────────────────────────────────────────────── */}
      <section aria-labelledby="rank-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-purple">
              By rank
            </p>
            <h2 id="rank-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Vacancies built around your rank — not keyword spam.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Search for a chief officer job on a generic board and you will
              wade through cadet ads, shore roles and outright scams wearing a
              &ldquo;chief officer&rdquo; label. On CrewAhead, rank is structured
              data. A vacancy has a rank, a department, a vessel type and a
              certificate requirement — and your profile has the same fields.
              Matching happens where the two meet.
            </p>
          </div>
          <div className="mt-12 space-y-10">
            {RANK_GROUPS.map((group, gi) => (
              <Reveal key={group.title} delay={gi * 0.05}>
                <div className="rounded-[1.75rem] border border-neon-purple/10 bg-panel/70 p-6 sm:p-8">
                  <h3 className="font-display text-xl font-bold text-neon-purple">
                    {group.title}
                  </h3>
                  <p className="mt-3 max-w-4xl text-sm leading-7 text-ash">
                    {group.intro}
                  </p>
                  <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {group.ranks.map((rank) => (
                      <li
                        key={rank.name}
                        className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3"
                      >
                        <p className="text-sm font-semibold text-foreground">{rank.name}</p>
                        <p className="mt-1 text-xs leading-5 text-ash">{rank.note}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Jobs by vessel type ──────────────────────────────────────────── */}
      <section
        aria-labelledby="vessel-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-green">
              By vessel type
            </p>
            <h2 id="vessel-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Tanker time is not container time. Matching that knows the
              difference.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Crewing decisions are made on vessel-type experience. An employer
              hiring a chief officer for a chemical tanker needs senior chemical
              tanker time; an offshore operator needs DP sea days. Every vacancy
              on CrewAhead carries its vessel category and type, and your sea
              service is recorded the same way — so the berths you see are the
              berths your sea time actually qualifies you for.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VESSEL_TYPES.map((vessel, i) => (
              <Reveal key={vessel.name} delay={i * 0.05}>
                <article className="h-full rounded-xl border border-neon-green/10 bg-panel/70 p-5 transition-colors hover:border-neon-green/30 hover:bg-panel-hover">
                  <h3 className="text-base font-semibold text-neon-green">{vessel.name}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{vessel.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Career intelligence ──────────────────────────────────────────── */}
      <section aria-labelledby="career-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              The differentiator
            </p>
            <h2 id="career-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Career intelligence no job board has: your sea time, mapped to
              what comes next.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Every maritime job board treats each contract as an isolated
              transaction. CrewAhead treats it as a step on a plotted course.
              The platform&apos;s career intelligence — live in your dashboard
              from the day you register — analyses your rank, sea service,
              vessel experience and certificates against hundreds of shore-side
              maritime roles, and tells you three things nobody else will: what
              you already qualify for, what you almost qualify for, and exactly
              what closes the distance.
            </p>
          </div>

          {careerDepth.length > 0 && (
            <div className="mt-10 grid gap-8 border-y border-white/10 py-10 sm:grid-cols-3">
              {careerDepth.map((item) => (
                <LiveStat key={item.label} value={item.value} label={item.label} />
              ))}
            </div>
          )}

          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {CAREER_FEATURES.map((feature, i) => (
              <Reveal key={feature.title} delay={i * 0.06}>
                <article className="h-full rounded-xl border border-neon-cyan/10 bg-panel/70 p-6 transition-colors hover:border-neon-cyan/30 hover:bg-panel-hover">
                  <div className="flex items-start gap-4">
                    <div className="rounded-lg border border-neon-cyan/15 bg-neon-cyan/8 p-2.5">
                      <feature.Icon className="h-5 w-5 text-neon-cyan" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold">{feature.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-ash">{feature.body}</p>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10">
            <div className="rounded-[1.75rem] border border-neon-purple/15 bg-panel/70 p-6 sm:p-8">
              <div className="grid gap-6 md:grid-cols-3">
                <div>
                  <p className="font-display text-sm font-bold text-neon-cyan">Chief Officer</p>
                  <p className="mt-1 flex items-center gap-2 text-xs text-ash">
                    becomes <ArrowRight className="h-3 w-3" aria-hidden="true" />
                  </p>
                  <p className="font-display text-sm font-bold text-neon-purple">
                    Port Operations Manager
                  </p>
                  <p className="mt-2 text-xs leading-5 text-ash">
                    Transfers: cargo planning, stability, port interface. Gap:
                    terminal systems, commercial contracts.
                  </p>
                </div>
                <div>
                  <p className="font-display text-sm font-bold text-neon-cyan">Second Engineer</p>
                  <p className="mt-1 flex items-center gap-2 text-xs text-ash">
                    becomes <ArrowRight className="h-3 w-3" aria-hidden="true" />
                  </p>
                  <p className="font-display text-sm font-bold text-neon-purple">
                    Technical Superintendent
                  </p>
                  <p className="mt-2 text-xs leading-5 text-ash">
                    Transfers: planned maintenance, diagnostics, dry-dock prep.
                    Gap: fleet budgeting, multi-vessel oversight.
                  </p>
                </div>
                <div>
                  <p className="font-display text-sm font-bold text-neon-cyan">Master</p>
                  <p className="mt-1 flex items-center gap-2 text-xs text-ash">
                    becomes <ArrowRight className="h-3 w-3" aria-hidden="true" />
                  </p>
                  <p className="font-display text-sm font-bold text-neon-purple">
                    Marine Manager / DPA
                  </p>
                  <p className="mt-2 text-xs leading-5 text-ash">
                    Transfers: ISM compliance, incident command, audit
                    readiness. Gap: shore-side SMS ownership, regulatory
                    reporting.
                  </p>
                </div>
              </div>
              <p className="mt-6 border-t border-white/10 pt-5 text-sm leading-6 text-ash">
                Whether you plan to sail for twenty more years or want out at
                the next port, the map is the same tool: know your readiness
                before you need it.{" "}
                <Link href="/about" className="font-semibold text-neon-cyan hover:text-neon-cyan/80">
                  Read why we built it
                </Link>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Documents ────────────────────────────────────────────────────── */}
      <section
        aria-labelledby="docs-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-amber">
              Documents &amp; compliance
            </p>
            <h2 id="docs-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Your CoC, endorsements and medicals — tracked before they lapse.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              A lapsed tanker endorsement or an expired medical does not just
              cost you a course fee — it quietly removes you from every
              shortlist that requires it, usually without telling you. On
              CrewAhead your passport, CDC, Certificate of Competency, flag
              endorsements, STCW certificates and medicals live in a document
              vault with expiry tracking and renewal warnings. When you apply,
              employers see verified, current documents — which is precisely
              why profiles with complete documentation get answered first.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <span className="pill-cyan">
                <FileText className="h-3 w-3" aria-hidden="true" /> Expiry tracking
              </span>
              <span className="pill-cyan">
                <BadgeCheck className="h-3 w-3" aria-hidden="true" /> Verified sharing
              </span>
              <span className="pill-cyan">
                <MessageSquare className="h-3 w-3" aria-hidden="true" /> Renewal warnings
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section aria-labelledby="faq-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              Questions seafarers actually ask
            </p>
            <h2 id="faq-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              Frequently asked questions
            </h2>
          </div>
          <div className="mt-10 space-y-4">
            {FAQ_ITEMS.map((item, i) => (
              <Reveal key={item.question} delay={i * 0.04}>
                <details className="group rounded-xl border border-white/10 bg-panel/70 p-5 transition-colors open:border-neon-cyan/25 hover:border-neon-cyan/20">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold [&::-webkit-details-marker]:hidden">
                    {item.question}
                    <ArrowRight
                      className="h-4 w-4 shrink-0 text-neon-cyan transition-transform group-open:rotate-90"
                      aria-hidden="true"
                    />
                  </summary>
                  <p className="mt-3 text-sm leading-7 text-ash">{item.answer}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ──────────────────────────────────────────────────── */}
      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-neon-cyan/15 bg-panel p-8 sm:p-12">
          <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 text-sm font-semibold text-neon-cyan">
                <Anchor className="h-4 w-4" aria-hidden="true" />
                Your next contract. Your plotted course.
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold sm:text-3xl">
                The berth is out there. So is the career after it. See both.
              </h2>
              <p className="mt-4 text-base leading-7 text-ash">
                Build your profile once. Apply to verified employers directly.
                Keep your documents current. And watch your sea time turn into
                a mapped career — at sea and ashore. Free for seafarers, and it
                stays that way.
              </p>
            </div>
            <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:items-center">
              <PrimaryCta large />
              <SignInLink className="text-center" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
